/**
 * OrganizationSettingsPage
 * CCH Care — Phase 5e
 * Last Modified: 2026-06-04
 *
 * Allows Care Admins to manage:
 * - Organization name and billing contact details
 * - Multiple facilities (add, edit) with address, phone, timezone,
 *   and supervisor self-review policy
 *
 * Accessible from More → Settings
 *
 * Changes (2026-06-04) — Supervisor self-review policy:
 * - Facility add/edit modal gains a "Documentation Policy" control for
 *   self_review_policy (flag | block | silent). 'flag' is the default
 *   and the legacy behavior. Facility cards show the current policy.
 *
 * Changes (2026-04-22) — Multi-facility support (Option A, admin-only):
 * - Facilities section lists ALL facilities in the tenant; each is a
 *   clickable card opening an edit modal. "+ Add Facility" opens the
 *   same modal in add mode. Case-insensitive dedupe (backend enforces).
 * - Load path supports { silent: true } to skip the page loading flash.
 *
 * ESLint: no-unused-vars, label-has-associated-control,
 *         click-events-have-key-events, no-unescaped-entities,
 *         no-autofocus (autoFocus on modal is the documented exception)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import {
  getSettings,
  updateOrgSettings,
  updateFacilitySettings,
  addFacility,
} from '../../api/admin';
import { COLORS } from '../../config/constants';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern Time (ET)'   },
  { value: 'America/Chicago',     label: 'Central Time (CT)'   },
  { value: 'America/Denver',      label: 'Mountain Time (MT)'  },
  { value: 'America/Phoenix',     label: 'Arizona Time (MST)'  },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)'   },
  { value: 'America/Anchorage',   label: 'Alaska Time (AKT)'   },
  { value: 'Pacific/Honolulu',    label: 'Hawaii Time (HST)'   },
];

// Supervisor self-review policy: what happens when a supervisor co-signs
// an ADL record on which they personally documented one or more sections.
const SELF_REVIEW_OPTIONS = [
  {
    value: 'flag',
    label: 'Allow & flag self-reviews',
    hint: 'Supervisors may co-sign records they documented; each is tagged Self-reviewed and recorded in the audit trail. Recommended.',
  },
  {
    value: 'block',
    label: 'Require an independent reviewer',
    hint: 'A supervisor cannot co-sign a record they personally documented — another supervisor or admin must review it.',
  },
  {
    value: 'silent',
    label: 'Allow silently (no visible marker)',
    hint: 'Supervisors may co-sign their own records with no visible flag. The audit trail still records the self-review.',
  },
];

function selfReviewLabel(value) {
  return SELF_REVIEW_OPTIONS.find(o => o.value === value)?.label
    ?? 'Allow & flag self-reviews';
}

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 8,
  border: `1px solid ${COLORS.border}`, fontSize: 14,
  color: COLORS.text, background: COLORS.surface,
  fontFamily: 'inherit', boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: COLORS.textMuted, marginBottom: 4,
};

/**
 * Decode HTML entities that PHP's htmlspecialchars() / sanitize() may have
 * introduced. Uses a textarea element so the browser decodes natively.
 */
function decodeHtml(str) {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

/** Apply decodeHtml to every string value in an object; non-strings pass through. */
function decodeAll(obj) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    out[k] = typeof v === 'string' ? decodeHtml(v) : (v ?? '');
  });
  return out;
}

export default function OrganizationSettingsPage() {
  const navigate  = useNavigate();
  const { memberships, checkSession } = useAuth();
  const role    = memberships?.[0]?.role ?? '';
  const isAdmin = ['Care Admin', 'Care Supervisor'].includes(role) || memberships?.[0]?.is_tenant_admin;

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  // Org form state
  const [orgForm, setOrgForm] = useState({
    organization_name:    '',
    billing_contact_name: '',
    billing_email:        '',
    billing_phone:        '',
  });
  const [orgSaving, setOrgSaving]   = useState(false);
  const [orgSuccess, setOrgSuccess] = useState(false);
  const [orgError, setOrgError]     = useState(null);

  // Facilities state — plural; modal-driven edit/add
  const [facilities, setFacilities]               = useState([]);
  const [editingFacility, setEditingFacility]     = useState(null); // null | 'new' | facility object
  const [facilitySuccessMsg, setFacilitySuccessMsg] = useState('');

  // `silent` loads skip the page-level loading spinner — used after
  // save actions so the page doesn't flash blank while refreshing.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getSettings();
      if (data.success) {
        const t    = data.tenant     || {};
        const facs = data.facilities || [];

        setOrgForm(decodeAll({
          organization_name:    t.tenant_name            ?? '',
          billing_contact_name: t.billing_contact_name   ?? '',
          billing_email:        t.billing_email          ?? '',
          billing_phone:        t.billing_phone          ?? '',
        }));

        setFacilities(facs.map(f => decodeAll({
          facility_id:        f.facility_id,
          name:               f.name          ?? '',
          address_line1:      f.address_line1 ?? '',
          address_line2:      f.address_line2 ?? '',
          city:               f.city          ?? '',
          state:              f.state         ?? '',
          zip_code:           f.zip_code      ?? '',
          phone:              f.phone         ?? '',
          timezone:           f.timezone      ?? 'America/New_York',
          self_review_policy: f.self_review_policy ?? 'flag',
        })));
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load settings');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function updateOrg(field, value) {
    setOrgForm(prev => ({ ...prev, [field]: value }));
    setOrgError(null);
    setOrgSuccess(false);
  }

  async function handleSaveOrg() {
    if (!orgForm.organization_name.trim()) {
      setOrgError('Organization name is required');
      return;
    }
    setOrgSaving(true);
    setOrgError(null);
    try {
      await updateOrgSettings(orgForm);
      setOrgSuccess(true);
      setTimeout(() => setOrgSuccess(false), 4000);
      await checkSession();
    } catch (e) {
      setOrgError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setOrgSaving(false);
    }
  }

  /**
   * Called by the modal on submit. Routes to add vs update based on
   * whether editingFacility is 'new' or an existing object.
   */
  async function handleSaveFacility(formData) {
    const isNew = editingFacility === 'new';
    if (isNew) {
      await addFacility(formData);
    } else {
      await updateFacilitySettings({
        facility_id: editingFacility.facility_id,
        ...formData,
      });
    }
    setEditingFacility(null);
    await load({ silent: true });
    flashFacilitySuccess(
      isNew
        ? `"${formData.name}" added successfully.`
        : `"${formData.name}" saved.`
    );
  }

  function flashFacilitySuccess(msg) {
    setFacilitySuccessMsg(msg);
    setTimeout(() => setFacilitySuccessMsg(''), 4000);
  }

  if (!isAdmin) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.text }}>Access Restricted</div>
        <div style={{ fontSize: 13, marginTop: 4 }}>Only Care Admins can manage organization settings.</div>
      </div>
    );
  }

  const facilityCountLabel = facilities.length === 0
    ? 'No facilities configured'
    : `${facilities.length} ${facilities.length === 1 ? 'facility' : 'facilities'}`;

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', background: COLORS.surface,
        borderBottom: `1px solid ${COLORS.border}`,
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/dashboard/more')}
          style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.primary, padding: '2px 6px' }}
        >
          ←
        </button>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.text }}>Organization Settings</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>Manage your organization and facility details</div>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMuted }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⚙️</div>
          Loading settings…
        </div>
      ) : error ? (
        <div style={{ margin: 16, padding: '12px 16px', borderRadius: 10, background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, color: COLORS.danger, fontSize: 13 }}>
          ⚠️ {error}
          <button onClick={() => load()} style={{ marginLeft: 12, background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>
            Retry
          </button>
        </div>
      ) : (
        <div style={{ padding: 16 }}>

          {/* ── Organization Section ── */}
          <SectionHeader icon="🏢" title="Organization" subtitle="Your company or operating entity details" />

          <div style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>
            <FormRow>
              <label htmlFor="org-name" style={labelStyle}>Organization Name *</label>
              <input
                id="org-name"
                style={inputStyle}
                value={orgForm.organization_name}
                onChange={e => updateOrg('organization_name', e.target.value)}
                placeholder="e.g. Sunrise Senior Care"
              />
            </FormRow>

            <SectionDivider label="Billing Contact" />

            <FormRow>
              <label htmlFor="billing-contact-name" style={labelStyle}>Contact Name</label>
              <input
                id="billing-contact-name"
                style={inputStyle}
                value={orgForm.billing_contact_name}
                onChange={e => updateOrg('billing_contact_name', e.target.value)}
                placeholder="Full name of billing contact"
              />
            </FormRow>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label htmlFor="billing-email" style={labelStyle}>Billing Email</label>
                <input
                  id="billing-email"
                  style={inputStyle}
                  type="email"
                  value={orgForm.billing_email}
                  onChange={e => updateOrg('billing_email', e.target.value)}
                  placeholder="billing@yourorg.com"
                />
              </div>
              <div>
                <label htmlFor="billing-phone" style={labelStyle}>Billing Phone</label>
                <input
                  id="billing-phone"
                  style={inputStyle}
                  type="tel"
                  value={orgForm.billing_phone}
                  onChange={e => updateOrg('billing_phone', e.target.value)}
                  placeholder="(555) 000-0000"
                />
              </div>
            </div>

            {orgError && <ErrorBanner message={orgError} />}
            {orgSuccess && <SuccessBanner message="Organization settings saved successfully." />}

            <button
              onClick={handleSaveOrg}
              disabled={orgSaving}
              style={{
                width: '100%', padding: '11px 0', borderRadius: 8, border: 'none',
                background: orgSaving ? COLORS.border : COLORS.primary,
                color: orgSaving ? COLORS.textMuted : '#fff',
                fontSize: 14, fontWeight: 700, cursor: orgSaving ? 'not-allowed' : 'pointer',
              }}
            >
              {orgSaving ? '⏳ Saving…' : 'Save Organization Settings'}
            </button>
          </div>

          {/* ── Facilities Section ── */}
          <SectionHeader
            icon="🏥"
            title="Facilities"
            subtitle={facilityCountLabel}
          />

          <div style={{ background: COLORS.surface, borderRadius: 12, border: `1px solid ${COLORS.border}`, padding: 16, marginBottom: 16 }}>

            {facilitySuccessMsg && <SuccessBanner message={facilitySuccessMsg} />}

            {facilities.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px', color: COLORS.textMuted, fontSize: 13, marginBottom: 12 }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>🏥</div>
                <div style={{ fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>No facilities yet</div>
                <div>Add your first facility to get started.</div>
              </div>
            ) : (
              <div style={{ marginBottom: 12 }}>
                {facilities.map(f => (
                  <FacilityCard
                    key={f.facility_id}
                    facility={f}
                    onEdit={() => setEditingFacility(f)}
                  />
                ))}
              </div>
            )}

            <button
              onClick={() => setEditingFacility('new')}
              style={{
                width: '100%', padding: '11px 0', borderRadius: 8,
                border: `1px dashed ${COLORS.primary}`,
                background: 'none', color: COLORS.primary,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}
            >
              + Add Facility
            </button>
          </div>

        </div>
      )}

      {/* Edit / Add modal */}
      {editingFacility && (
        <FacilityFormModal
          facility={editingFacility === 'new' ? null : editingFacility}
          onSave={handleSaveFacility}
          onClose={() => setEditingFacility(null)}
        />
      )}
    </div>
  );
}

// ── Facility card ─────────────────────────────────────────────

function FacilityCard({ facility, onEdit }) {
  const cityStateZip = [
    facility.city,
    facility.state,
  ].filter(Boolean).join(', ') + (facility.zip_code ? ` ${facility.zip_code}` : '');

  const addressLine = [facility.address_line1, cityStateZip.trim()]
    .filter(s => s && s.trim())
    .join(' · ');

  const tzLabel = TIMEZONES.find(tz => tz.value === facility.timezone)?.label || facility.timezone;
  const footerLine = [tzLabel, facility.phone].filter(Boolean).join(' · ');

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEdit(); } }}
      style={{
        background: COLORS.bg, borderRadius: 10, padding: '12px 14px',
        border: '1px solid transparent',
        display: 'flex', alignItems: 'center', gap: 12,
        cursor: 'pointer', marginBottom: 8,
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={e => e.currentTarget.style.borderColor = COLORS.primary}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
    >
      <span style={{ fontSize: 24, flexShrink: 0 }} aria-hidden="true">🏥</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, marginBottom: 2 }}>
          {facility.name}
        </div>
        {addressLine && (
          <div style={{ fontSize: 12, color: COLORS.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {addressLine}
          </div>
        )}
        {footerLine && (
          <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {footerLine}
          </div>
        )}
        <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>
          Self-review: {selfReviewLabel(facility.self_review_policy)}
        </div>
      </div>
      <span style={{ fontSize: 16, color: COLORS.textMuted, flexShrink: 0 }}>›</span>
    </div>
  );
}

// ── Facility form modal (add / edit) ──────────────────────────

function FacilityFormModal({ facility, onSave, onClose }) {
  const isNew = !facility;

  const [form, setForm] = useState({
    name:               facility?.name               ?? '',
    address_line1:      facility?.address_line1       ?? '',
    address_line2:      facility?.address_line2       ?? '',
    city:               facility?.city                ?? '',
    state:              facility?.state               ?? '',
    zip_code:           facility?.zip_code            ?? '',
    phone:              facility?.phone               ?? '',
    timezone:           facility?.timezone            ?? 'America/New_York',
    self_review_policy: facility?.self_review_policy  ?? 'flag',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  function update(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErr(null);
  }

  async function handleSubmit() {
    if (!form.name.trim()) {
      setErr('Facility name is required');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      await onSave(form);
      // Parent closes the modal on success — don't setSaving(false) here
      // because unmount is imminent.
    } catch (e) {
      setErr(e?.response?.data?.error || e.message || 'Failed to save facility');
      setSaving(false);
    }
  }

  const selfReviewHint = SELF_REVIEW_OPTIONS.find(o => o.value === form.self_review_policy)?.hint ?? '';

  return (
    <>
      {/* Backdrop */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Close modal"
        onClick={saving ? undefined : onClose}
        onKeyDown={e => {
          if (!saving && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClose();
          }
        }}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 200,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        background: COLORS.surface, borderRadius: 12,
        padding: 24, width: '90%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        zIndex: 201,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: COLORS.text }}>
            {isNew ? 'Add Facility' : 'Edit Facility'}
          </h3>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none', border: 'none', fontSize: 18,
              cursor: saving ? 'not-allowed' : 'pointer',
              color: COLORS.textMuted, padding: '4px 8px',
              opacity: saving ? 0.5 : 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <FormRow>
          <label htmlFor="mf-name" style={labelStyle}>Facility Name *</label>
          <input
            id="mf-name"
            style={inputStyle}
            value={form.name}
            onChange={e => update('name', e.target.value)}
            placeholder="e.g. Sunrise North Campus"
            autoFocus
          />
        </FormRow>

        <SectionDivider label="Address" />

        <FormRow>
          <label htmlFor="mf-addr1" style={labelStyle}>Address Line 1</label>
          <input
            id="mf-addr1"
            style={inputStyle}
            value={form.address_line1}
            onChange={e => update('address_line1', e.target.value)}
            placeholder="Street address"
          />
        </FormRow>

        <FormRow>
          <label htmlFor="mf-addr2" style={labelStyle}>Address Line 2</label>
          <input
            id="mf-addr2"
            style={inputStyle}
            value={form.address_line2}
            onChange={e => update('address_line2', e.target.value)}
            placeholder="Suite, floor, unit (optional)"
          />
        </FormRow>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label htmlFor="mf-city" style={labelStyle}>City</label>
            <input
              id="mf-city"
              style={inputStyle}
              value={form.city}
              onChange={e => update('city', e.target.value)}
              placeholder="City"
            />
          </div>
          <div>
            <label htmlFor="mf-state" style={labelStyle}>State</label>
            <select
              id="mf-state"
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={form.state}
              onChange={e => update('state', e.target.value)}
            >
              <option value="">&mdash;</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="mf-zip" style={labelStyle}>ZIP</label>
            <input
              id="mf-zip"
              style={inputStyle}
              value={form.zip_code}
              onChange={e => update('zip_code', e.target.value)}
              placeholder="ZIP"
              maxLength={10}
            />
          </div>
        </div>

        <SectionDivider label="Contact &amp; Timezone" />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label htmlFor="mf-phone" style={labelStyle}>Facility Phone</label>
            <input
              id="mf-phone"
              style={inputStyle}
              type="tel"
              value={form.phone}
              onChange={e => update('phone', e.target.value)}
              placeholder="(555) 000-0000"
            />
          </div>
          <div>
            <label htmlFor="mf-tz" style={labelStyle}>Timezone</label>
            <select
              id="mf-tz"
              style={{ ...inputStyle, cursor: 'pointer' }}
              value={form.timezone}
              onChange={e => update('timezone', e.target.value)}
            >
              {TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>
        </div>

        <SectionDivider label="Documentation Policy" />

        <FormRow>
          <label htmlFor="mf-self-review" style={labelStyle}>Supervisor Self-Review</label>
          <select
            id="mf-self-review"
            style={{ ...inputStyle, cursor: 'pointer' }}
            value={form.self_review_policy}
            onChange={e => update('self_review_policy', e.target.value)}
          >
            {SELF_REVIEW_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 6, lineHeight: 1.5 }}>
            {selfReviewHint}
          </div>
        </FormRow>

        {err && <ErrorBanner message={err} />}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 8,
              border: `1px solid ${COLORS.border}`,
              background: COLORS.surface, color: COLORS.textMuted,
              fontSize: 14, fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              flex: 2, padding: '10px 0', borderRadius: 8, border: 'none',
              background: saving ? COLORS.border : COLORS.primary,
              color: saving ? COLORS.textMuted : '#fff',
              fontSize: 14, fontWeight: 700,
              cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving
              ? '⏳ Saving…'
              : (isNew ? 'Add Facility' : 'Save Changes')}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Shared sub-components ─────────────────────────────────────

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: COLORS.textMuted }}>{subtitle}</div>}
      </div>
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: COLORS.textMuted,
      textTransform: 'uppercase', letterSpacing: '0.05em',
      borderBottom: `1px solid ${COLORS.border}`,
      paddingBottom: 4, marginBottom: 12, marginTop: 4,
    }}>
      {label}
    </div>
  );
}

function FormRow({ children }) {
  return <div style={{ marginBottom: 12 }}>{children}</div>;
}

function ErrorBanner({ message }) {
  return (
    <div style={{
      padding: '9px 12px', borderRadius: 8, marginBottom: 12,
      background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
      fontSize: 13, color: COLORS.danger, fontWeight: 600,
    }}>
      ⚠️ {message}
    </div>
  );
}

function SuccessBanner({ message }) {
  return (
    <div style={{
      padding: '9px 12px', borderRadius: 8, marginBottom: 12,
      background: COLORS.accentLight, border: `1px solid ${COLORS.accentAlpha}`,
      fontSize: 13, color: COLORS.accent, fontWeight: 600,
    }}>
      ✅ {message}
    </div>
  );
}